import './SubscriptionForm.scss';

import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { dictionariesSelector, layoutSelector } from '../../../store';
import { mutationFooterForm } from '../../../network/api/form/mutationFooterForm';
import { Typography } from '@dataartdev/uikit/Typography';
import { Button } from '@dataartdev/uikit/Button';
import { Checkbox } from '@dataartdev/uikit/Checkbox';
import { TextField } from '@dataartdev/uikit/TextField';
import { IconArrowRight } from '@dataartdev/uikit/IconArrowRight';
import * as yup from 'yup';
import { useFormik } from 'formik';
import TagManager from 'react-gtm-module';
import Subscription from '../../../assets/img/subscription.png';
import ReCAPTCHA from 'react-google-recaptcha';
import { recaptchaFormKey, recaptchaKey } from '../../../common/constants';
import { cn } from '../../../ui/utils/bem';

const cnSubscriptionForm = cn('SubscriptionForm');

export const SubscriptionForm: React.FC = () => {
  const [isSubmited, setIsSubmited] = useState(false);
  const { companySiteBlogPost } = useSelector(dictionariesSelector) || {};
  const { pageId } = useSelector(layoutSelector);
  const {
    companySiteBlogPostFormRequiredField = 'This field is required',
    companySiteBlogPostFormSubscribeEventSuccessTitle = 'Success!',
    companySiteBlogPostFormSubscribeEventSuccessText = "You've been successfully subscribed to our newsletter. Keep an eye out for our latest insights!",
    companySiteBlogPostFormSubscribeEventTrackingID = 'newsletter_form',
    // companySiteBlogPostFormSubscribeEventButton = 'Subscribe',
    companySiteBlogPostFormSubscribeEventTitle = 'Subscribe to Our Newsletter',
    companySiteBlogPostFormSubscribeEventText = 'Subscribe to our newsletter and never miss an interesting meeting or webinar',
    companySiteBlogPostFormSubscribeEventSubscribePolicy = 'I have read and agree to the Privacy Policy',
    companySiteBlogPostFormSubscribeEventEmail: {
      companySiteBlogPostFormSubscribeEventEmailText = '',
      companySiteBlogPostFormSubscribeEventEmailError = '',
    } = {},
    companySiteBlogPostFormSubscribeEventName,
  } =
    companySiteBlogPost?.companySiteBlogPostForm
      .companySiteBlogPostFormSubscribeEvent || {};

  const validationSchema = yup.object({
    name: yup
      .string()
      .min(3)
      .required(companySiteBlogPostFormRequiredField)
      .nullable(),
    email: yup
      .string()
      .email(companySiteBlogPostFormSubscribeEventEmailError)
      .required(companySiteBlogPostFormRequiredField)
      .nullable(),
    privacyAgreement: yup
      .boolean()
      .oneOf([true], 'on')
      .required(),
  });

  const initialValues = {
    name: '',
    email: '',
    privacyAgreement: false,
  };

  const reCaptchaRef = useRef<ReCAPTCHA>(null);

  const {
    handleSubmit,
    isSubmitting,
    errors,
    dirty,
    handleBlur,
    handleChange,
    values,
    touched,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (body, actions) => {
      try {
        const token = await reCaptchaRef.current?.executeAsync();
        await mutationFooterForm({
          body: {
            ...body,
            pageId: pageId,
            [`${recaptchaFormKey}`]: token,
          },
        });
        setIsSubmited(true);
        actions.resetForm();
        TagManager.dataLayer({
          dataLayer: {
            event: 'newsletter_subscription',
            form_name: companySiteBlogPostFormSubscribeEventTrackingID,
          },
        });
      } catch (error) {
        error instanceof Error
          ? console.error(error.message)
          : console.error(error);
      }
    },
  });

  const reCaptchaAvailability =
    !!recaptchaKey && process.env.NODE_ENV === 'production';

  return (
    <div className={cnSubscriptionForm()}>
      <div className={cnSubscriptionForm('Content', { isSuccess: isSubmited })}>
        <div className={cnSubscriptionForm('Info')}>
          {companySiteBlogPostFormSubscribeEventTitle && (
            <Typography.Title
              className={cnSubscriptionForm('Title')}
              size="lg"
              as="h2"
            >
              {companySiteBlogPostFormSubscribeEventTitle}
            </Typography.Title>
          )}
          {companySiteBlogPostFormSubscribeEventText && (
            <Typography.Text className={cnSubscriptionForm('Text')} size="m">
              {companySiteBlogPostFormSubscribeEventText}
            </Typography.Text>
          )}
        </div>
        <form className={cnSubscriptionForm('Form')} onSubmit={handleSubmit}>
          <div className={cnSubscriptionForm('Fields')}>
            <TextField
              name="name"
              label={companySiteBlogPostFormSubscribeEventName}
              view="outline"
              width="full"
              onChange={({ e }) => handleChange(e)}
              onBlur={handleBlur}
              value={values.name}
              required
              caption={touched.name && errors.name ? errors.name : undefined}
              status={touched.name && !!errors.name ? 'alert' : undefined}
            />
            <TextField
              name="email"
              label={companySiteBlogPostFormSubscribeEventEmailText}
              view="outline"
              width="full"
              onChange={({ e }) => handleChange(e)}
              onBlur={handleBlur}
              value={values.email}
              required
              caption={touched.email && errors.email ? errors.email : undefined}
              status={touched.email && !!errors.email ? 'alert' : undefined}
            />
          </div>
          <div className={cnSubscriptionForm('Bottom')}>
            <Checkbox
              required
              label={companySiteBlogPostFormSubscribeEventSubscribePolicy}
              name="privacyAgreement"
              className={cnSubscriptionForm('Checkbox')}
              onChange={({ e }) => handleChange(e)}
              onBlur={handleBlur}
              checked={values.privacyAgreement}
            />
            <Button
              className={cnSubscriptionForm('Submit')}
              id={companySiteBlogPostFormSubscribeEventTrackingID}
              iconRight={IconArrowRight}
              disabled={
                isSubmitting ||
                !!Object.keys(errors).length ||
                !dirty ||
                isSubmited
              }
              type="submit"
            />
          </div>
          {reCaptchaAvailability && (
            <ReCAPTCHA
              ref={reCaptchaRef}
              sitekey={recaptchaKey}
              size="invisible"
            />
          )}
        </form>
      </div>
      <div className={cnSubscriptionForm('Success', { isSuccess: isSubmited })}>
        <div className={cnSubscriptionForm('Success-Text')}>
          {companySiteBlogPostFormSubscribeEventSuccessTitle && (
            <Typography.Title
              className={cnSubscriptionForm('Title')}
              size="lg"
              as="h2"
            >
              {companySiteBlogPostFormSubscribeEventSuccessTitle}
            </Typography.Title>
          )}
          {companySiteBlogPostFormSubscribeEventSuccessText && (
            <Typography.Text className={cnSubscriptionForm('Text')} size="m">
              {companySiteBlogPostFormSubscribeEventSuccessText}
            </Typography.Text>
          )}
        </div>
        <div className={cnSubscriptionForm('Success-Img')}>
          <img src={Subscription} alt="Success" />
        </div>
      </div>
    </div>
  );
};
